/**
 * Shared reconnect timing constants. Exported so consumers (e.g.
 * ChatWSClient in @circuit/chat) can import the same values rather than
 * inlining their own — prevents the two clients from drifting apart on
 * reconnect bounds.
 */
export const RECONNECT_MIN_MS = 1_000;
export const RECONNECT_MAX_MS = 30_000;

/**
 * Reconnect backoff with full jitter.
 *
 * Spreads reconnect attempts across a random window so a mass disconnect
 * (pod restart, deploy, regional blip) doesn't make every client reconnect
 * in synchronized waves and stampede the server. Full jitter only lowers
 * each delay's upper bound while keeping the mean similar, so it can't
 * regress behaviour relative to plain exponential backoff.
 *
 * The SSE request-retry path has an app-local twin (`computeBackoffDelay` in
 * @circuit/app's retryWithBackoff) using the same full-jitter strategy.
 */
export interface JitteredBackoffOptions {
  /** 0-indexed attempt count (e.g. `reconnectAttempts`). */
  attempt: number;
  /** Base delay for attempt 0, in ms. */
  minMs: number;
  /** Upper bound the exponential is capped at, in ms. */
  maxMs: number;
  /** Injectable randomness for deterministic tests. Defaults to Math.random. */
  random?: () => number;
}

/** Full-jitter delay in `[exp/2, exp]` where `exp = min(minMs * 2 ** attempt, maxMs)`. */
export function jitteredBackoffDelay(opts: JitteredBackoffOptions): number {
  const { attempt, minMs, maxMs, random = Math.random } = opts;
  const exp = Math.min(minMs * 2 ** attempt, maxMs);
  return Math.floor(exp / 2 + random() * (exp / 2));
}
